"use client";
import { useState, useEffect, useCallback } from "react";
import { Plus, Search, X, Mic, ChevronDown, Pencil, Trash2 } from "lucide-react";

interface Podcast {
  id: number;
  contactName: string;
  podcastName: string;
  email: string;
  website: string;
  dateReachedOut: string;
  followUpDate: string;
  status: string;
  direction: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

const STATUSES = [
  { value: "reached_out", label: "Reached Out", color: "#3b82f6" },
  { value: "follow_up_sent", label: "Follow-Up Sent", color: "#f59e0b" },
  { value: "booked", label: "Booked", color: "#8b5cf6" },
  { value: "recorded", label: "Recorded", color: "#10b981" },
  { value: "published", label: "Published", color: "#06b6d4" },
  { value: "no_response", label: "No Response", color: "#6b7280" },
  { value: "declined", label: "Declined", color: "#ef4444" },
];

const DIRECTIONS = [
  { value: "guesting", label: "Guesting (I'm on their show)" },
  { value: "hosting", label: "Hosting (They're on my show)" },
  { value: "swap", label: "Swap (Both)" },
];

function StatusBadge({ status }: { status: string }) {
  const s = STATUSES.find((x) => x.value === status) || STATUSES[0];
  return (
    <span
      style={{ background: s.color + "22", color: s.color, border: `1px solid ${s.color}44` }}
      className="px-2 py-0.5 rounded-full text-xs font-medium"
    >
      {s.label}
    </span>
  );
}

function DirectionBadge({ direction }: { direction: string }) {
  const d = DIRECTIONS.find((x) => x.value === direction) || DIRECTIONS[0];
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--bg-hover)] text-[var(--text-muted)]">
      {d.label}
    </span>
  );
}

export function PodcastCRM() {
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({
    contactName: "",
    podcastName: "",
    email: "",
    website: "",
    dateReachedOut: new Date().toISOString().slice(0, 10),
    followUpDate: "",
    status: "reached_out",
    direction: "guesting",
    notes: "",
  });

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterStatus !== "all") params.set("status", filterStatus);
    if (search) params.set("search", search);
    const res = await fetch(`/api/podcasts?${params}`);
    setPodcasts(await res.json());
  }, [filterStatus, search]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({
      contactName: "", podcastName: "", email: "", website: "",
      dateReachedOut: new Date().toISOString().slice(0, 10),
      followUpDate: "", status: "reached_out", direction: "guesting", notes: "",
    });
    setEditId(null);
    setShowAdd(false);
  };

  const save = async () => {
    if (editId) {
      await fetch(`/api/podcasts/${editId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
    } else {
      await fetch("/api/podcasts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
    }
    resetForm();
    load();
  };

  const startEdit = (p: Podcast) => {
    setForm({
      contactName: p.contactName,
      podcastName: p.podcastName,
      email: p.email,
      website: p.website,
      dateReachedOut: p.dateReachedOut,
      followUpDate: p.followUpDate,
      status: p.status,
      direction: p.direction,
      notes: p.notes,
    });
    setEditId(p.id);
    setShowAdd(true);
  };

  const remove = async (id: number) => {
    if (!confirm("Delete this podcast entry?")) return;
    await fetch(`/api/podcasts/${id}`, { method: "DELETE" });
    load();
  };

  const updateStatus = async (id: number, status: string) => {
    await fetch(`/api/podcasts/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    load();
  };

  const isOverdue = (p: Podcast) => {
    if (!p.followUpDate || p.status === "booked" || p.status === "recorded" || p.status === "published" || p.status === "declined") return false;
    return new Date(p.followUpDate) < new Date();
  };

  const counts = STATUSES.reduce((acc, s) => {
    acc[s.value] = podcasts.filter((p) => p.status === s.value).length;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Mic size={24} className="text-[var(--accent)]" />
          <h2 className="text-xl font-bold">Podcast CRM</h2>
          <span className="text-sm text-[var(--text-muted)]">
            {podcasts.length} total
          </span>
        </div>
        <button
          onClick={() => { resetForm(); setShowAdd(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--accent)] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <Plus size={16} /> Add Podcast
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-6">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilterStatus(filterStatus === s.value ? "all" : s.value)}
            className={`p-3 rounded-lg text-center transition-all border ${
              filterStatus === s.value
                ? "border-[var(--accent)] bg-[var(--accent)]10"
                : "border-[var(--border)] bg-[var(--bg-card)]"
            }`}
          >
            <div className="text-lg font-bold" style={{ color: s.color }}>{counts[s.value] || 0}</div>
            <div className="text-[10px] text-[var(--text-muted)]">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          type="text"
          placeholder="Search by name, podcast, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm"
        />
      </div>

      {/* Add/Edit Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg)] border border-[var(--border)] rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">{editId ? "Edit" : "Add"} Podcast</h3>
              <button onClick={resetForm}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Contact Name *</label>
                <input className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Podcast Name</label>
                <input className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm" value={form.podcastName} onChange={(e) => setForm({ ...form, podcastName: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">Email</label>
                  <input className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">Website</label>
                  <input className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">Date Reached Out</label>
                  <input type="date" className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm" value={form.dateReachedOut} onChange={(e) => setForm({ ...form, dateReachedOut: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">Follow-Up Date</label>
                  <input type="date" className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm" value={form.followUpDate} onChange={(e) => setForm({ ...form, followUpDate: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">Status</label>
                  <select className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">Direction</label>
                  <select className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                    {DIRECTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Notes</label>
                <textarea rows={3} className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={save} className="flex-1 py-2.5 bg-[var(--accent)] text-white rounded-lg text-sm font-medium hover:opacity-90">
                {editId ? "Save Changes" : "Add Podcast"}
              </button>
              <button onClick={resetForm} className="px-4 py-2.5 border border-[var(--border)] rounded-lg text-sm hover:bg-[var(--bg-hover)]">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Podcast</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Reached Out</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Follow-Up</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Direction</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {podcasts.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-[var(--text-muted)]">No podcasts yet. Add your first one!</td></tr>
              )}
              {podcasts.map((p) => (
                <tr key={p.id} className={`border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors ${isOverdue(p) ? "bg-red-500/5" : ""}`}>
                  <td className="px-4 py-3 font-medium">{p.contactName}</td>
                  <td className="px-4 py-3">{p.podcastName}</td>
                  <td className="px-4 py-3 text-[var(--text-muted)]">{p.email}</td>
                  <td className="px-4 py-3 text-[var(--text-muted)]">{p.dateReachedOut}</td>
                  <td className={`px-4 py-3 ${isOverdue(p) ? "text-red-400 font-medium" : "text-[var(--text-muted)]"}`}>
                    {p.followUpDate || "—"}
                    {isOverdue(p) && " ⚠️"}
                  </td>
                  <td className="px-4 py-3"><DirectionBadge direction={p.direction} /></td>
                  <td className="px-4 py-3">
                    <div className="relative group inline-block">
                      <button className="flex items-center gap-1">
                        <StatusBadge status={p.status} />
                        <ChevronDown size={12} className="text-[var(--text-muted)]" />
                      </button>
                      <div className="absolute left-0 top-full mt-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-xl z-10 hidden group-hover:block min-w-[160px]">
                        {STATUSES.map((s) => (
                          <button
                            key={s.value}
                            onClick={() => updateStatus(p.id, s.value)}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-hover)] first:rounded-t-lg last:rounded-b-lg"
                          >
                            <StatusBadge status={s.value} />
                          </button>
                        ))}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => startEdit(p)} className="p-1.5 rounded hover:bg-[var(--bg-hover)]">
                        <Pencil size={14} className="text-[var(--text-muted)]" />
                      </button>
                      <button onClick={() => remove(p.id)} className="p-1.5 rounded hover:bg-red-500/10">
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes preview */}
      {podcasts.filter((p) => p.notes).length > 0 && (
        <div className="mt-4 text-xs text-[var(--text-muted)]">
          {podcasts.filter((p) => p.notes).map((p) => (
            <div key={p.id} className="mb-1">
              <strong>{p.contactName}:</strong> {p.notes}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
